import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Images, ZoomIn, ChevronsLeftRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { services, portfolioItems } from '../../mock';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import { Lightbox, buildSlides } from '../Lightbox';
import { useSwipe } from '../../hooks/useSwipe';

const latestProjects = [...portfolioItems].sort((a, b) => b.id - a.id).slice(0, 4);
const serviceTitle = (id) => services.find((s) => s.id === id)?.title || { sl: '', en: '', it: '' };

export const LatestProjects = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrent((prev) => (prev + 1) % latestProjects.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % latestProjects.length);
  const prev = () => setCurrent((prev) => (prev - 1 + latestProjects.length) % latestProjects.length);
  const open = (project) => setLightbox({ slides: buildSlides(project), title: t(project.title) });
  const swipe = useSwipe(next, prev);

  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" data-testid="latest-projects-section">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-yellow-500/10 blur-3xl pointer-events-none" />
      <div className="container mx-auto px-5 md:px-10 relative z-10">
        <Reveal className="text-center mb-16">
          <h2 className="heading-4 mb-4 gradient-text-primary">
            {t({ sl: 'NAŠI ZADNJI PROJEKTI', en: 'OUR LATEST PROJECTS', it: 'I NOSTRI ULTIMI PROGETTI' })}
          </h2>
          <p className="body-medium text-[#b0b0b0] max-w-2xl mx-auto">
            {t({
              sl: 'Sveži pogled v naše najnovejše izvedbe – od polepitev vozil do tiska na tekstil.',
              en: 'A fresh look at our newest work – from vehicle wraps to textile printing.',
              it: 'Uno sguardo fresco ai nostri ultimi lavori – dai wrapping veicoli alla stampa tessile.'
            })}
          </p>
        </Reveal>

        <Reveal className="relative max-w-6xl mx-auto" y={40} delay={0.1}>
          <div className="overflow-hidden rounded-2xl border border-white/10" {...swipe} data-testid="latest-projects-carousel">
            <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${current * 100}%)` }}>
              {latestProjects.map((project, idx) => (
                <div key={project.id} className="w-full flex-shrink-0" aria-hidden={idx !== current} inert={idx !== current ? true : undefined}>
                  <div className="grid grid-cols-1 md:grid-cols-2 bg-[#141414]">
                    <div
                      className={`relative h-72 md:h-[480px] cursor-pointer group overflow-hidden ${project.whiteBg ? 'bg-white' : 'bg-black'}`}
                      onClick={() => open(project)}
                      data-testid={`latest-project-image-${project.id}`}
                    >
                      <img
                        src={project.image}
                        alt={t(project.title)}
                        className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${project.whiteBg ? 'object-contain p-6' : 'object-cover'}`}
                      />
                      <div className="absolute top-4 right-4 flex gap-2">
                        {project.beforeAfter && (
                          <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1.5">
                            <ChevronsLeftRight className="w-4 h-4 text-white" />
                            <span className="text-white text-sm font-medium">{t({ sl: 'Prej / Potem', en: 'Before / After', it: 'Prima / Dopo' })}</span>
                          </div>
                        )}
                        {project.gallery && project.gallery.length > 1 && (
                          <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1.5">
                            <Images className="w-4 h-4 text-white" />
                            <span className="text-white text-sm font-medium">{project.gallery.length}</span>
                          </div>
                        )}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                          <ZoomIn className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>

                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <span className="inline-block w-fit text-xs uppercase tracking-[0.2em] font-semibold mb-4 px-3 py-1 rounded-full border border-white/20 bg-gradient-to-r from-cyan-400/10 via-purple-500/10 to-yellow-400/10 text-white">
                        {t(serviceTitle(project.category))}
                      </span>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Exo 2', sans-serif" }}>
                        {t(project.title)}
                      </h3>
                      <p className="body-medium text-[#b0b0b0] mb-8">{t(project.description)}</p>
                      <div className="flex flex-wrap gap-3">
                        <Button onClick={() => open(project)} className="btn-secondary" data-testid={`latest-project-zoom-${project.id}`}>
                          <ZoomIn className="mr-2 w-4 h-4" />
                          {t({ sl: 'Pogled', en: 'View', it: 'Guarda' })}
                        </Button>
                        <Link to="/portfolio">
                          <Button className="btn-primary" data-testid={`latest-project-more-${project.id}`}>
                            {t({ sl: 'Več', en: 'More', it: 'Altro' })}
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={prev} className="hidden md:flex absolute left-2 md:-left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 z-10 transition-colors" aria-label="Previous project" data-testid="latest-projects-prev">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button onClick={next} className="hidden md:flex absolute right-2 md:-right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 z-10 transition-colors" aria-label="Next project" data-testid="latest-projects-next">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={prev} className="md:hidden w-11 h-11 flex items-center justify-center bg-white/10 rounded-full" aria-label="Previous project" data-testid="latest-projects-prev-mobile">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex gap-2">
            {latestProjects.map((project, idx) => (
              <button
                key={project.id}
                onClick={() => setCurrent(idx)}
                className={`h-2.5 rounded-full transition-all ${idx === current ? 'bg-white w-8' : 'bg-white/40 w-2.5'}`}
                aria-label={`Go to project ${idx + 1}`}
                data-testid={`latest-projects-dot-${idx}`}
              />
            ))}
            </div>
            <button onClick={next} className="md:hidden w-11 h-11 flex items-center justify-center bg-white/10 rounded-full" aria-label="Next project" data-testid="latest-projects-next-mobile">
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button className="btn-secondary" data-testid="latest-projects-view-all">
                {t({ sl: 'Poglej vse projekte', en: 'View All Projects', it: 'Vedi Tutti i Progetti' })}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </Reveal>
      </div>

      {lightbox && (
        <Lightbox slides={lightbox.slides} title={lightbox.title} onClose={() => setLightbox(null)} testId="latest-projects-lightbox" />
      )}
    </section>
  );
};
